import { AfterViewChecked, AfterViewInit, Component, Input, NgZone, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Installer } from '../../../../domains/users';
import { KudosReason } from '../../../../domains/users/kudos-reason/kudos-reason.model';
import { ReputationScoreWithTier } from '../../../../core/installer-profile';
import * as am5 from '@amcharts/amcharts5';
import * as am5radar from '@amcharts/amcharts5/radar';
import * as am5xy from '@amcharts/amcharts5/xy';
import { createAm5Root } from '../../../../shared/charts';

@Component({
  selector: 'gc-installer-reputation-score',
  templateUrl: './installer-reputation-score.component.html',
  styleUrls: ['./installer-reputation-score.component.scss'],
})
export class InstallerReputationScoreComponent implements OnDestroy, AfterViewInit, AfterViewChecked, OnChanges {
  @Input() installer!: Installer;
  @Input() reputationScore?: ReputationScoreWithTier;
  @Input() kudosReason?: KudosReason;

  constructor(
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  getScore(): number {
    const score = this.reputationScore?.reputationScore ?? 0;
    return Math.min(Math.max(Math.floor(score), 0), 100);
  }

  getCompletedProjectReviews(): number {
    return this.kudosReason?.completedProjectReviews ?? 0;
  }

  private kudosReasonsRoot?: am5.Root;
  private chartInitialized = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reputationScore'] || changes['kudosReason']) {
      if (this.chartInitialized) {
        this.chartInitialized = false;
      }
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    this.updateChart();
  }

  ngAfterViewChecked() {
    if (!this.chartInitialized && this.shouldRenderChart()) {
      this.updateChart();
    }
  }

  private shouldRenderChart(): boolean {
    if (!this.reputationScore || !this.reputationScore.reputationScore) {
      return false;
    }
    const element = document.getElementById('kudosReasonsRadarChart1');
    return element !== null;
  }

  private updateChart() {
    if (!this.shouldRenderChart()) {
      return;
    }

    Promise.resolve().then(() => {
      this.zone.runOutsideAngular(() => {
        this.kudosReasonsRadarChart();
        this.changeDetectorRef.detectChanges();
      });
    });
  }
  kudosReasonsRadarChart() {
    const element = document.getElementById('kudosReasonsRadarChart1');
    if (!element) {
      console.warn('Chart element kudosReasonsRadarChart1 not found, skipping chart initialization');
      return;
    }

    if (this.kudosReasonsRoot) {
      this.kudosReasonsRoot.dispose();
      this.kudosReasonsRoot = undefined;
    }
    this.kudosReasonsRoot = createAm5Root('kudosReasonsRadarChart1');

    let chart = this.kudosReasonsRoot.container.children.push(
      am5radar.RadarChart.new(this.kudosReasonsRoot, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
      }),
    );

    const cursor = chart.set(
      'cursor',
      am5radar.RadarCursor.new(this.kudosReasonsRoot, {
        behavior: 'none',
      }),
    );

    cursor.lineY.set('forceHidden', true);

    let xRenderer = am5radar.AxisRendererCircular.new(this.kudosReasonsRoot, {
      strokeOpacity: 0.1,
      minGridDistance: 10,
    });
    xRenderer.labels.template.setAll({
      forceHidden: true
    });

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.kudosReasonsRoot, {
        categoryField: 'category',
        renderer: xRenderer,
      }),
    );

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.kudosReasonsRoot, {
        min: 0,
        max: 10,
        renderer: am5radar.AxisRendererRadial.new(this.kudosReasonsRoot, { radius: am5.percent(90) }),
      }),
    );
    yAxis.get('renderer').labels.template.setAll({
      forceHidden: true
    });
    let series = chart.series.push(
      am5radar.RadarLineSeries.new(this.kudosReasonsRoot, {
        name: 'Kudos',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'count',
        categoryXField: 'category',
        stroke: am5.color('#FF8C00'),
        fill: am5.color('#FF8C00'),
        opacity: 0.5,
        tooltip: am5.Tooltip.new(this.kudosReasonsRoot, {
          labelText: '{categoryX}: {valueY}',
        }), 
      }),
    );

    series.strokes.template.setAll({
      strokeWidth: 2,
    });

    series.bullets.push(() => {
      const circle = am5.Circle.new(this.kudosReasonsRoot!, {
        radius: 5,
        fill: series.get('fill'),
        shadowColor: am5.color('#000000'),
        shadowBlur: 8,
        shadowOpacity: 0.5,
      });
      return am5.Bullet.new(this.kudosReasonsRoot!, {
        sprite: circle,
      });
    });
    
    const getAverage = (name: string): number => {
      if (this.kudosReason) {
        const value = (this.kudosReason as any)[name];
        return value !== null && value !== undefined ? value : 5;
      }
      return 5;
    };
    
    let data = [
      {
        name: 'professionalismAverage',
        count: getAverage('professionalismAverage'),
        category: 'Professionalism',
      },
      {
        name: 'dependabilityAverage',
        count: getAverage('dependabilityAverage'),
        category: 'Dependability',
      },
      {
        name: 'punctualityAverage',
        count: getAverage('punctualityAverage'),
        category: 'Punctuality',
      },
      {
        name: 'qualityAverage',
        count: getAverage('qualityAverage'),
        category: 'Quality',
      },
      {
        name: 'attitudeAverage',
        count: getAverage('attitudeAverage'),
        category: 'Attitude',
      },
      {
        name: 'timelyCommunicationAverage',
        count: getAverage('timelyCommunicationAverage'),
        category: 'Timely Communication',
      },
      {
        name: 'likelyToUseAgainAverage',
        count: getAverage('likelyToUseAgainAverage'),
        category: 'Likely To Hire Again',
      },
    ]

    xAxis.data.setAll(data);
    series.data.setAll(data);

    series.appear(1200);
    chart.appear(1200, 250);
    this.chartInitialized = true;
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      this.kudosReasonsRoot?.dispose();
    });
  }
}
